import React, { useEffect, useRef, useState } from "react";
import "../../assets/styles/BiometricVerification.css";
import BiometricService from "../../services/biometricService";

interface BiometricVerificationProps {
  onVerified: () => void;
  userId: string;
}

const BiometricVerification: React.FC<BiometricVerificationProps> = ({ onVerified, userId }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const biometricService = BiometricService.getInstance();

  useEffect(() => {
    let stream: MediaStream | null = null;

    const startCamera = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setIsScanning(true);
      } catch (err) {
        setError("Failed to access camera. Please ensure camera permissions are granted.");
        console.error("Camera access error:", err);
      }
    };

    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const handleVerification = async () => {
    try {
      if (!videoRef.current?.srcObject) {
        throw new Error("No video stream available");
      }

      const stream = videoRef.current.srcObject as MediaStream;
      const faceData = await biometricService.captureFaceData(stream);
      
      // Store the face data
      await biometricService.storeBiometricData(userId, faceData);
      
      // Stop the camera
      stream.getTracks().forEach(track => track.stop());
      
      onVerified();
    } catch (err) {
      setError("Failed to capture face data. Please try again.");
      console.error("Verification error:", err);
    }
  };

  return (
    <div className="biometric-container">
      {/* Header */}
      <header className="biometric-header">
        <div className="logo">
          <div className="logo-circle">L</div>
          <span className="app-name">LavernAI</span>
        </div>
      </header>

      {/* Main Content */}
      <div className="biometric-main">
        <div className="camera-frame">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camera-feed"
          />
          {error && <div className="error-message">{error}</div>}
        </div>
        <h1>Biometric Verification</h1>
        <p>Please look directly at the camera for facial recognition.</p>
        <div className="scanning-status">
          <span className={`status-dot ${isScanning ? 'scanning' : ''}`}></span>
          <span>{isScanning ? 'Scanning in progress...' : 'Ready to scan'}</span>
        </div>
      </div>

      {/* Footer with Continue Button */}
      <div className="biometric-footer">
        <button 
          className="continue-button" 
          onClick={handleVerification}
          disabled={!isScanning || !!error}
        >
          Capture & Continue
        </button>
      </div>
    </div>
  );
};

export default BiometricVerification;
